import React, { useState, useContext, useMemo, useCallback } from 'react';
import GoTrue, { User } from 'gotrue-js';
import { useHistory, useLocation } from 'react-router-dom';

const auth = new GoTrue({
  APIUrl: process.env.REACT_APP_IDENTITY_API_URL,
});

type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string, remember: boolean) => Promise<void>;
  logout: () => Promise<void>;
  signup: (email: string, password: string, data: object) => Promise<User>;
  acceptInvite: (password: string, data: object) => Promise<User>;
  isInvite: boolean;
};

const AuthContext = React.createContext<undefined | AuthContextType>(undefined);

type Props = {
  children: React.ReactNode;
};

const AuthProvider = ({ children }: Props) => {
  const [user, setUser] = useState<User | null>(auth.currentUser());
  const history = useHistory();
  const location = useLocation();
  const { current: inviteToken } = React.useRef(location.hash.split('=')[1]);

  console.log(inviteToken);

  const login = useCallback(
    async (email: string, password: string, remember: boolean) => {
      return auth.login(email, password, remember).then(setUser);
    },
    []
  );

  const logout = useCallback(
    async () => user?.logout().then(() => setUser(null)),
    [user]
  );

  const acceptInvite = useCallback(
    async (password: string, data: object) => {
      return auth.acceptInvite(inviteToken, password).then(user => {
        return user.update({ data });
      });
    },
    [inviteToken]
  );

  const signup = useCallback(
    async (email: string, password: string, data: object) => {
      return auth.signup(email, password, data);
    },
    []
  );

  const context = useMemo(
    () => ({
      isAuthenticated: Boolean(user),
      isInvite: Boolean(inviteToken),
      user,
      login,
      logout,
      signup,
      acceptInvite,
    }),
    [user, login, logout, signup, acceptInvite, inviteToken]
  );

  React.useEffect(() => {
    if (inviteToken) {
      history.push('/signup');
    }
  }, [history, inviteToken]);

  return (
    <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("Missing provider 'AuthProvider'");
  }

  return context;
};

export { AuthProvider, useAuth };
